import protocolSender from "../util/protocol-sender"
import { RecvState, MediaType, MediaOper, EventType } from "../../common/enums"
import { promiseHandler, hstRequest } from "../../common/promise-handler"
import logger from "../../common/logger"
import { tracelog, getBitRate } from "../../common/common"
import mediaManager from "./media-manager"
import mediaMapping from './media-mapping'
import { getEventPostfix } from '../../common/common'

class VideoManager {
  constructor() {
    // User defined video parameters
    // Media Id: { width: xx, height: xx, frameRate: xx, bitRate: xx }
    this.videoParamMap = new Map();

    // Default video parameters
    this.defaultVideoParam = { frameRate: 15, width: 640, height: 480, bitRate: 0 };
  }
  /**
   * 开始广播视频
   * @param {*} deviceId 
   * @param {*} videoParam 
   */
  @tracelog
  startPublishVideo(deviceId, videoParam) {
    if (!deviceId) { // TODO：不允许默认设备
      logger.error("Invalid deviceId!");
      return;
    }

    // videoParam: { width: 640, height: 360, frameRate: 15, bitRate: 1000 }
    if (videoParam) { 
      if (!('width' in videoParam) || typeof videoParam.width !== 'number') {
        logger.error("No width or invalid width in videoParam!");
        return;
      }

      if (!('height' in videoParam) || typeof videoParam.height !== 'number') {
        logger.error("No height or invalid height in videoParam!");
        return;
      }
      
      if (!('frameRate' in videoParam) || typeof videoParam.frameRate !== 'number') {
        logger.error("No frameRate or invaid frameRate in videoParam!");
        return;
      }
      
      if (!('bitRate' in videoParam) || typeof videoParam.bitRate !== 'number') {
        logger.error("No bitRate or invalid bitRate in videoParam!");
        return;
      }
      // 保存视频参数
      this.setVideoParam(deviceId, videoParam);
    }

    protocolSender.sendPublishMediaReq(MediaOper.START_PUBLISH, MediaType.VIDEO, deviceId);
    
    // 发布响应（暂未实现）
    // return promiseHandler(EventType.EVENT_START_PUB_VIDEO);
  };

  /**
   * 停止广播视频
   * @param {*} deviceId 
   */
  @tracelog
  stopPublishVideo(deviceId) {
    protocolSender.sendPublishMediaReq(MediaOper.STOP_PUBLISH, MediaType.VIDEO, deviceId);

    // 取消发布响应（暂未实现）
    // return promiseHandler(EventType.EVENT_STOP_PUB_VIDEO);
  };

  /**
   * 开始接收远端视频
   * @param {*} userId 
   * @param {*} mediaId 
   */
  @tracelog
  startReceiveVideo(userId, mediaId) {
    return hstRequest.request({
      eventName: EventType.EVENT_START_RCV_MEDIA + getEventPostfix(userId, MediaType.VIDEO, mediaId),
      retryConfig: {
        num: 0,
        max: 4
      },
      requestFn: () => {
        protocolSender.sendRecvMediaReq(
          RecvState.START_RECV, 
          userId, 
          MediaType.VIDEO,
          mediaId);
      }
    })
    // protocolSender.sendRecvMediaReq(
    //   RecvState.START_RECV, 
    //   userId, 
    //   MediaType.VIDEO,
    //   mediaId);

    // // 可能同时接收多个视频，保证请求和响应匹配
    // return promiseHandler(EventType.EVENT_START_RCV_MEDIA + getEventPostfix(userId, MediaType.VIDEO, mediaId));
  };

  /**
   * 停止接收远端视频
   * @param {*} userId 
   * @param {*} mediaId 
   */
  @tracelog
  stopReceiveVideo(userId, mediaId) {
    
    protocolSender.sendRecvMediaReq(
      RecvState.STOP_RECV, 
      userId, 
      MediaType.VIDEO, 
      mediaId);

    return promiseHandler(EventType.EVENT_STOP_RCV_MEDIA + getEventPostfix(userId, MediaType.VIDEO, mediaId));
  };

  /**
   * 开始预览本地视频
   * @param {*} mediaId 
   * @param {*} videoEle 
   * @param {*} videoParam 
   */
  @tracelog
  setLocalVideoRender(mediaId, videoEle, videoParam) {
    if (!videoEle || !('srcObject' in videoEle)){
      logger.error("Invalid videoEle!");
      return;
    }

    if (!mediaId) {
      logger.error("Invalid mediaId!");
      return;
    }

    // videoParam: { width: 640, height: 360, frameRate: 15 }
    if (videoParam) {
      if (!('width' in videoParam) || typeof videoParam.width !== 'number') {
        logger.error("No width or invalid width in videoParam!");
        return;
      }

      if (!('height' in videoParam) || typeof videoParam.height !== 'number') {
        logger.error("No height or invalid height in videoParam!");
        return;
      }
      
      if (!('frameRate' in videoParam) || typeof videoParam.frameRate !== 'number') {
        logger.error("No frameRate or invaid frameRate in videoParam!");
        return;
      }
    }
    let constrants = null;
    if (videoParam) {
      constrants = {
        video: {
          deviceId: 
          {
            exact: mediaMapping.getMappingDevId(MediaType.VIDEO, mediaId)
          },
          frameRate:  videoParam.frameRate ,
          width:  videoParam.width ,
          height: videoParam.height 
        }
      }
    } else {
      constrants = mediaManager.constructConstraints({
        mediaType: MediaType.VIDEO,
        mediaId: mediaId
      });
    }

    // this.unsetLocalVideoRender(videoEle)  修复大小屏切换 黑屏问题

    return new Promise((resolve, reject) => {
      navigator.mediaDevices.getUserMedia(constrants).then((stream) => {
        videoEle.srcObject = stream;
        resolve()
      }).catch((error) => {
        logger.error("Get stream failed!" + error);
        reject("Get stream failed!" + error)
      })
    })
  }

  /**
   * 取消预览本地视频
   * @param {*} videoEle 
   */
  @tracelog
  unsetLocalVideoRender(videoEle) {
    if (!videoEle || !('srcObject' in videoEle)){
      logger.error("Invalid videoEle!");
      return;
    }
    if (videoEle.srcObject) {
        for (const track of videoEle.srcObject.getTracks()) {
          track.stop();
        }
        videoEle.srcObject = null;
    } else {
      logger.warn("Invalid srcObject!");
    }
  }
  /**
   * 获取视频流码流限制
   * @param {*} devId 
   */
  getVideoBitRate(devId) {
    let videoParam = this.videoParamMap.get(devId);
    if (!videoParam) {
      videoParam = this.defaultVideoParam;
    }
    if (!videoParam.bitRate || videoParam.bitRate == 0) {
      return getBitRate(videoParam.width, videoParam.height, videoParam.frameRate);
    } else {
      return videoParam.bitRate;
    }
  }

  /**
   * 设置视频参数
   * @param {*} mediaId 
   * @param {*} params 
   */
  setVideoParam(mediaId, params) {
    this.videoParamMap.set(mediaId, params);
  }

  /**
   * 获取视频参数
   * @param {*} mediaId 
   * @param {*} params 
   */
  getVideoParam(mediaId) {
    return this.videoParamMap.get(mediaId);
  }

  /**
   * 构造视频采集constraints
   * @param {*} mediaId 
   * @param {*} devId 
   */
  buildConstraints(mediaId, devId) {
    let videoConstraints = { deviceId: {exact: devId}};
    let videoParam = this.videoParamMap.get(mediaId);
    if (videoParam) {
      videoConstraints.frameRate = { ideal: videoParam.frameRate };
      videoConstraints.width = videoParam.width
      videoConstraints.height = videoParam.height
    } else {
      videoConstraints.frameRate = { ideal: this.defaultVideoParam.frameRate };
      videoConstraints.width = this.defaultVideoParam.width
      videoConstraints.height = this.defaultVideoParam.height
    }
    return { video: videoConstraints };
  }
}

export default new VideoManager();
